import type { FormErrors } from 'src/lib/components/form/formTypes';
import camelCase from 'lodash/camelCase';
import mapKeys from 'lodash/mapKeys';

export interface FieldErrors {
    // TODO: Refactor typing implementation for errors
    // to better fit structure of form values and support
    // nested errors
    [key: string]: any;
}

type GraphQLValidationPrimitive = string | number | boolean | null;
type GraphQLValidationObject = {
    readonly [key: string]: GraphQLValidationValue;
};
export type GraphQLValidationValue =
    | GraphQLValidationPrimitive
    | readonly GraphQLValidationValue[]
    | GraphQLValidationObject;

export interface GraphQLErrorExtensionsLike {
    code?: string;
    fields?: GraphQLValidationValue;
    field_codes?: GraphQLValidationValue;
    original_error?: string;
}

export interface GraphQLErrorLike {
    message?: string;
    extensions?: GraphQLErrorExtensionsLike;
}

export type NormalizedGraphQLFieldErrors = Record<string, string[]>;

/**
 * Lightweight interface for Apollo errors after v4 migration.
 * Apollo Client 4 removes the ApolloError type and ErrorHandlerOptions helpers
 * that exposed graphQLErrors, networkError, and response properties.
 */
export interface ApolloErrorLike {
    graphQLErrors?: GraphQLErrorLike[];
    networkError?: Error | { statusCode?: number } | null;
    response?: object | null;
    message?: string;
}

const GLOBAL_GRAPHQL_ERROR_KEY = camelCase('__all__');
const SERIALIZED_VALIDATION_LIST_ITEM_PATTERN = /'((?:\\'|[^'])*)'|"((?:\\"|[^"])*)"/g;

const isFieldErrorRecord = (
    value: GraphQLValidationValue | undefined,
): value is GraphQLValidationObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const parseSerializedValidationMessages = (value: string): string[] | null => {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
        return null;
    }

    const messages = Array.from(trimmed.matchAll(SERIALIZED_VALIDATION_LIST_ITEM_PATTERN)).map(
        (match) => (match[1] ?? match[2]).replace(/\\'/g, "'").replace(/\\"/g, '"'),
    );

    return messages.length > 0 ? messages : null;
};

const normalizeGraphQLValidationValue = (value: GraphQLValidationValue | undefined): string[] => {
    if (value == null) {
        return [];
    }
    if (typeof value === 'string') {
        return parseSerializedValidationMessages(value) ?? [value];
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return [String(value)];
    }
    if (Array.isArray(value)) {
        return value.flatMap(normalizeGraphQLValidationValue);
    }
    return Object.values(value).flatMap(normalizeGraphQLValidationValue);
};

export const normalizeGraphQLFieldErrors = (
    fields: GraphQLValidationValue | undefined,
): NormalizedGraphQLFieldErrors => {
    if (!fields) {
        return {};
    }

    if (!isFieldErrorRecord(fields)) {
        const globalErrors = normalizeGraphQLValidationValue(fields);
        return globalErrors.length > 0 ? { [GLOBAL_GRAPHQL_ERROR_KEY]: globalErrors } : {};
    }

    const normalizedFieldErrors: NormalizedGraphQLFieldErrors = {};
    Object.entries(fields).forEach(([key, value]) => {
        normalizedFieldErrors[camelCase(key)] = normalizeGraphQLValidationValue(value);
    });
    return normalizedFieldErrors;
};

export const getGraphQLValidationErrorMessages = (
    fields: GraphQLValidationValue | undefined,
): string[] => {
    const normalizedFieldErrors = normalizeGraphQLFieldErrors(fields);
    const globalErrors = normalizedFieldErrors[GLOBAL_GRAPHQL_ERROR_KEY];

    if (Array.isArray(globalErrors) && globalErrors.length > 0) {
        return globalErrors;
    }

    return Object.values(normalizedFieldErrors).flatMap((value) =>
        normalizeGraphQLValidationValue(value),
    );
};

export const hasGraphQLValidationCode = (
    error: ApolloErrorLike | undefined,
    expectedCode: string,
): boolean => {
    if (!error?.graphQLErrors || !expectedCode) {
        return false;
    }

    return error.graphQLErrors.some((graphQLError) => {
        if (graphQLError.extensions?.code !== 'ValidationError') {
            return false;
        }

        const normalizedFieldCodes = normalizeGraphQLFieldErrors(
            graphQLError.extensions?.field_codes,
        );
        return Object.values(normalizedFieldCodes).some((codes) => codes.includes(expectedCode));
    });
};

export const getGraphQLFieldErrors = (
    error: ApolloErrorLike | undefined,
): NormalizedGraphQLFieldErrors => {
    if (!error) {
        return {};
    }
    if (!error.graphQLErrors || error.graphQLErrors.length === 0) {
        return {};
    }
    return normalizeGraphQLFieldErrors(error.graphQLErrors[0]?.extensions?.fields);
};

export const mergeFormErrorsToFieldErrors = (
    formErrors: FormErrors<any>,
    fieldErrors: FieldErrors,
) => {
    const mergedFieldErrors: FieldErrors = { ...fieldErrors };
    mapKeys(formErrors, (value, key) => {
        const formError = value as string;
        mergedFieldErrors[key] = mergedFieldErrors[key] || [];
        mergedFieldErrors[key]!.unshift(formError);
    });
    return mergedFieldErrors;
};

export function mergeErrors(
    error1: string | string[] | undefined,
    error2: string | string[] | undefined,
) {
    const errors: string[] = [];
    if (error1) {
        if (Array.isArray(error1)) {
            errors.push(...error1);
        } else {
            errors.push(error1);
        }
    }
    if (error2) {
        if (Array.isArray(error2)) {
            errors.push(...error2);
        } else {
            errors.push(error2);
        }
    }
    if (errors.length === 0) {
        return undefined;
    }
    if (errors.length === 1) {
        return errors[0];
    }
    return errors;
}

/**
 * Extracts object-level errors from array field errors.
 * Array errors can be either string (array-level) or object (field-level).
 * This helper safely returns only the object errors for passing to individual field components.
 */
export function getArrayFieldObjectError<T>(
    arrayFieldError: string | FormErrors<T> | undefined,
): FormErrors<T> | undefined {
    if (!arrayFieldError) return undefined;
    if (typeof arrayFieldError === 'string') return undefined;
    if (Array.isArray(arrayFieldError)) return undefined;
    return arrayFieldError;
}
